import React, { useState } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { clearAppCache } from '../utils/clearCache';

interface ClearCacheButtonProps {
  variant?: 'pill' | 'compact' | 'icon' | 'full';
  className?: string;
  lang?: 'en' | 'bn';
}

/**
 * ClearCacheButton Component
 * Reusable button to unregister Service Workers, clear Cache Storage, and trigger a hard reload.
 */
export default function ClearCacheButton({
  variant = 'pill',
  className = '',
  lang = 'bn'
}: ClearCacheButtonProps) {
  const [clearing, setClearing] = useState(false);

  const handleClearCache = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clearing) return;
    setClearing(true);
    await clearAppCache();
  };

  const labelText = lang === 'en' ? 'Clear Cache' : 'ক্যাশ ক্লিয়ার করুন';

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClearCache}
        disabled={clearing}
        title={labelText}
        aria-label={labelText}
        className={`p-2 rounded-full bg-neutral-100 hover:bg-rose-50 dark:bg-white/5 dark:hover:bg-rose-950/30 text-neutral-600 dark:text-neutral-300 hover:text-rose-500 transition-all cursor-pointer ${className}`}
      >
        {clearing ? (
          <Loader2 className="w-4 h-4 animate-spin text-rose-500" />
        ) : (
          <RotateCcw className="w-4 h-4" />
        )}
      </button>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={handleClearCache}
        disabled={clearing}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/20 transition-all cursor-pointer active:scale-95 ${className}`}
      >
        {clearing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RotateCcw className="w-3.5 h-3.5" />
        )}
        <span>{clearing ? (lang === 'en' ? 'Clearing...' : 'ক্লিয়ার হচ্ছে...') : labelText}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClearCache}
      disabled={clearing}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs font-black bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white shadow-md shadow-rose-500/20 transition-all cursor-pointer active:scale-95 disabled:opacity-75 ${className}`}
    >
      {clearing ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <RotateCcw className="w-4 h-4" />
      )}
      <span>{clearing ? (lang === 'en' ? 'Resetting Cache...' : 'রিফ্রেশ হচ্ছে...') : labelText}</span>
    </button>
  );
}
